"use client";

/**
 * stats renderer — mirrors the "Safety & Trust" / "Why share with us" 3-card
 * row layout: eyebrow + heading + subtitle, then a centered grid of stat
 * cards. Each card shows an icon (or numeric value), a short title, and a
 * brief supporting line.
 *
 * Data:
 *   - section.content { eyebrow, title, subtitle, columns? }
 *   - section.cards [{ icon, title, description }]
 */

import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";
import { SafeIcon } from "./SafeIcon";
import { CardDescriptionHtml } from "@/components/visual-cms/CardDescriptionHtml";

function readString(c: unknown, key: string): string {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const v = (c as Record<string, unknown>)[key];
    return typeof v === "string" ? v : "";
  }
  return "";
}

function readColumns(c: unknown): 2 | 3 | 4 {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const v = (c as Record<string, unknown>).columns;
    if (typeof v === "number" && [2, 3, 4].includes(v)) return v as 2 | 3 | 4;
  }
  return 3;
}

const gridByCols: Record<2 | 3 | 4, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

export function StatsSection({ section }: { section: VisualCmsSection }) {
  const shell = sectionShellStyle(section);
  const content = (section.content as Record<string, unknown>) ?? {};
  const eyebrow = readString(content, "eyebrow");
  const title = readString(content, "title");
  const subtitle = readString(content, "subtitle");
  const cols = readColumns(content);
  const items = (section.cards ?? []).filter((c) => c.is_active !== false);

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        {(eyebrow || title || subtitle) && (
          <div className="text-center max-w-2xl mx-auto mb-10">
            {/* {eyebrow && (
              <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-green-500 mb-2">
                {eyebrow}
              </p>
            )} */}
            {title && (
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900">
                {title}
              </h2>
            )}
            {subtitle && <p className="mt-2 text-base text-gray-500">{subtitle}</p>}
          </div>
        )}

        <div className={`grid grid-cols-1 ${gridByCols[cols]} gap-5 lg:gap-6`}>
          {items.map((it) => (
            <article
              key={it.id}
              className="rounded-xl border border-gray-200 bg-white p-6 lg:p-8 text-center shadow-[0_1px_2px_rgba(15,77,31,0.04)] hover:border-green-300 hover:shadow-[0_10px_28px_-14px_rgba(15,77,31,0.2)] transition-all duration-200"
            >
              <div className="relative inline-flex mb-5">
                <div className="absolute inset-0 rounded-full bg-green-200 blur-md opacity-55" />
                <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white shadow-[0_8px_18px_-6px_rgba(30,140,58,0.5)]">
                  <SafeIcon name={it.icon} className="h-6 w-6" />
                </div>
              </div>
              {it.title && (
                <h3 className="font-semibold text-lg text-gray-900 mb-2">{it.title}</h3>
              )}
              {it.description && (
                <CardDescriptionHtml
                  as="p"
                  text={it.description}
                  className="text-sm text-gray-500 leading-relaxed"
                />
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
